<template>
  <div class="group-manager">
    <!-- Header -->
    <div class="gm-header">
      <span class="gm-count">{{ groups.length }} groups</span>
      <button class="gm-new-btn" @click="startCreateGroup">
        + New group
      </button>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="gm-loading">
      <div class="gm-spinner"></div>
    </div>

    <!-- Empty State -->
    <div v-else-if="groups.length === 0" class="gm-empty">
      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
        <path d="M19 21V5a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v5m-4 0h4"/>
      </svg>
      <p class="gm-empty-title">No indicator groups yet</p>
      <span class="gm-empty-desc">Create a group to save indicator combinations</span>
      <button class="gm-empty-action" @click="startCreateGroup">Create first group</button>
    </div>

    <!-- Groups List -->
    <div v-else class="gm-groups-list">
      <div 
        v-for="group in groups" 
        :key="group.id"
        class="gm-group-card"
      >
        <div class="gm-group-top">
          <span class="gm-group-name">
            {{ group.name }}
            <span v-if="group.isDefault" class="gm-default-badge">Default</span>
          </span>
          <div class="gm-group-actions">
            <button class="gm-action-btn gm-action-apply" @click="applyGroup(group)" title="Apply to chart">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M3 3v18h18"/>
                <path d="M7 14l3-3 3 2 5-6"/>
              </svg>
            </button>
            <button
              class="gm-action-btn gm-action-default"
              :class="{ active: group.isDefault }"
              @click="setDefaultGroup(group)"
              :title="group.isDefault ? 'Default group' : 'Make default'"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" :fill="group.isDefault ? 'currentColor' : 'none'" stroke="currentColor" stroke-width="2" stroke-linejoin="round">
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
              </svg>
            </button>
            <button class="gm-action-btn gm-action-edit" @click="editGroup(group)" title="Edit group">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
              </svg>
            </button>
            <button
              class="gm-action-btn gm-action-delete"
              :class="{ disabled: group.isDefault }"
              :disabled="group.isDefault"
              @click="confirmDelete(group)"
              :title="group.isDefault ? 'Default group cannot be deleted' : 'Delete group'"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="3 6 5 6 21 6"/>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
              </svg>
            </button>
          </div>
        </div>
        <div class="gm-group-tags">
          <span 
            v-for="ind in group.indicators.slice(0, 5)" 
            :key="ind.name"
            class="gm-tag"
            :class="{ 'gm-tag-hidden': ind.hidden }"
          >{{ ind.name }}</span>
          <span v-if="group.indicators.length > 5" class="gm-tag gm-tag-more">
            +{{ group.indicators.length - 5 }}
          </span>
        </div>
      </div>
    </div>

    <!-- Create/Edit Modal -->
    <div v-if="showFormModal" class="gm-modal-overlay" @click="closeModal">
      <div class="gm-modal" @click.stop>
        <div class="gm-modal-header">
          <h3>{{ editingGroup ? 'Edit Group' : 'New Group' }}</h3>
          <button class="gm-modal-close" @click="closeModal">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
        
        <div class="gm-modal-body">
          <div class="gm-form-group">
            <label class="gm-form-label">Group Name</label>
            <input 
              v-model="formData.name"
              class="gm-form-input"
              type="text"
              placeholder="e.g., Trend setup"
              ref="groupNameInput"
            />
          </div>
          
          <div class="gm-form-group">
            <label class="gm-form-label">Description</label>
            <textarea 
              v-model="formData.description"
              class="gm-form-textarea"
              placeholder="Optional description"
              rows="2"
            ></textarea>
          </div>

          <label class="gm-default-toggle">
            <input type="checkbox" v-model="formData.isDefault" />
            <span>Use as default group on reload</span>
          </label>

          <div class="gm-form-group">
            <label class="gm-form-label">Indicators ({{ formData.indicators.length }})</label>
            <div class="gm-picker">
              <div class="gm-picker-search">
                <input
                  v-model="indicatorSearch"
                  class="gm-form-input"
                  type="text"
                  placeholder="Search indicators to add"
                />
              </div>
              <div class="gm-picker-list">
                <button
                  v-for="indicator in filteredAvailableIndicators"
                  :key="indicator.name"
                  class="gm-picker-item"
                  :class="{ selected: hasFormIndicator(indicator) }"
                  @click="toggleFormIndicator(indicator)"
                >
                  <span>
                    {{ indicator.name }}
                    <small v-if="indicator.description">{{ indicator.description }}</small>
                  </span>
                  <svg v-if="hasFormIndicator(indicator)" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                  <svg v-else width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <line x1="12" y1="5" x2="12" y2="19"/>
                    <line x1="5" y1="12" x2="19" y2="12"/>
                  </svg>
                </button>
              </div>
            </div>
            <div v-if="formData.indicators.length === 0" class="gm-empty-indicators">
              <p>No indicators selected</p>
              <span>Search and add indicators above</span>
            </div>
            <div v-else class="gm-selected-indicators-list">
              <div 
                v-for="(indicator, index) in formData.indicators" 
                :key="indicator.name"
                class="gm-indicator-row"
                :class="{ 'gm-indicator-hidden': indicator.hidden }"
              >
                <button 
                  class="gm-visibility-btn" 
                  @click="toggleIndicatorVisibility(index)"
                  :title="indicator.hidden ? 'Show indicator' : 'Hide indicator'"
                >
                  <svg v-if="!indicator.hidden" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                  <svg v-else width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                    <line x1="1" y1="1" x2="23" y2="23"/>
                  </svg>
                </button>
                <span class="gm-indicator-name" :class="{ 'gm-name-hidden': indicator.hidden }">{{ indicator.name }}</span>
                <button class="gm-remove-indicator" @click="removeFormIndicator(index)">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <line x1="18" y1="6" x2="6" y2="18"/>
                    <line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="gm-modal-footer">
          <button class="gm-btn gm-btn-secondary" @click="closeModal">Cancel</button>
          <button 
            class="gm-btn gm-btn-primary" 
            @click="saveGroup"
            :disabled="!formData.name.trim() || formData.indicators.length === 0"
          >
            {{ editingGroup ? 'Update' : 'Create' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation -->
    <div v-if="deletingGroup" class="gm-modal-overlay" @click="cancelDelete">
      <div class="gm-modal gm-modal-sm" @click.stop>
        <div class="gm-modal-header">
          <h3>Delete Group</h3>
        </div>
        <div class="gm-modal-body">
          <p class="gm-delete-msg">Delete <strong>"{{ deletingGroup.name }}"</strong>? This can't be undone.</p>
        </div>
        <div class="gm-modal-footer">
          <button class="gm-btn gm-btn-secondary" @click="cancelDelete">Cancel</button>
          <button class="gm-btn gm-btn-danger" @click="deleteGroup">Delete</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import indicatorGroupsService from '../services/indicatorGroups.js'
import notifications from '../utils/notifications.js'

export default {
  name: 'GroupManager',
  props: {
    currentIndicators: {
      type: Array,
      default: () => []
    },
    availableIndicators: {
      type: Array,
      default: () => []
    }
  },
  emits: ['apply-group', 'group-selected', 'groups-loaded'],
  data() {
    return {
      groups: [],
      loading: false,
      showFormModal: false,
      editingGroup: null,
      deletingGroup: null,
      indicatorSearch: '',
      formData: {
        name: '',
        description: '',
        indicators: [],
        isDefault: false
      }
    }
  },
  computed: {
    filteredAvailableIndicators() {
      const query = this.indicatorSearch.trim().toLowerCase()
      const list = this.availableIndicators || []
      if (!query) return list
      return list.filter(indicator => {
        const name = (indicator.name || '').toLowerCase()
        const type = (indicator.type || '').toLowerCase()
        const description = (indicator.description || '').toLowerCase()
        return name.includes(query) || type.includes(query) || description.includes(query)
      })
    }
  },
  async mounted() {
    await this.loadGroups()
  },
  methods: {
    async loadGroups() {
      this.loading = true
      try {
        this.groups = await indicatorGroupsService.getIndicatorGroups()
        this.$emit('groups-loaded', this.groups.length)
      } catch (error) {
        console.error('Failed to load indicator groups:', error)
        notifications.error('Failed to load indicator groups')
      } finally {
        this.loading = false
      }
    },

    startCreateGroup() {
      this.editingGroup = null
      this.formData = {
        name: '',
        description: '',
        indicators: [...this.currentIndicators],
        isDefault: false
      }
      this.indicatorSearch = ''
      this.showFormModal = true
      this.$nextTick(() => {
        if (this.$refs.groupNameInput) {
          this.$refs.groupNameInput.focus()
        }
      })
    },

    editGroup(group) {
      this.editingGroup = group
      this.formData = {
        name: group.name,
        description: group.description || '',
        indicators: [...group.indicators],
        isDefault: !!group.isDefault
      }
      this.indicatorSearch = ''
      this.showFormModal = true
      this.$nextTick(() => {
        if (this.$refs.groupNameInput) {
          this.$refs.groupNameInput.focus()
        }
      })
    },

    async saveGroup() {
      try {
        const groupData = {
          name: this.formData.name.trim(),
          description: this.formData.description.trim(),
          indicators: [...this.formData.indicators],
          isDefault: !!this.formData.isDefault
        }

        indicatorGroupsService.validateIndicatorGroup(groupData)

        if (this.editingGroup) {
          // If renamed, update Legend's custom_groups localStorage
          if (this.editingGroup.name !== groupData.name) {
            this._syncLegendGroupRename(this.editingGroup.name, groupData.name)
          }
          await indicatorGroupsService.updateIndicatorGroup(this.editingGroup.id, groupData)
          notifications.success('Group updated')
        } else {
          const saved = await indicatorGroupsService.addIndicatorGroup(groupData)
          this._syncLegendGroupAdd(saved.name)
          notifications.success('Group created')
        }

        await this.loadGroups()
        this.closeModal()
      } catch (error) {
        console.error('Failed to save group:', error)
        notifications.error(error.message || 'Failed to save group')
      }
    },

    confirmDelete(group) {
      if (group.isDefault) {
        notifications.error('Default group cannot be deleted')
        return
      }
      this.deletingGroup = group
    },

    async deleteGroup() {
      try {
        this._syncLegendGroupRemove(this.deletingGroup.name)
        await indicatorGroupsService.deleteIndicatorGroup(this.deletingGroup.id)
        notifications.success('Group deleted')
        await this.loadGroups()
        this.cancelDelete()
      } catch (error) {
        console.error('Failed to delete group:', error)
        notifications.error('Failed to delete group')
      }
    },

    applyGroup(group) {
      this.$emit('apply-group', group)
    },

    async setDefaultGroup(group) {
      try {
        await indicatorGroupsService.setDefaultIndicatorGroup(group.id)
        notifications.success(`"${group.name}" is now default`)
        await this.loadGroups()
      } catch (error) {
        console.error('Failed to set default group:', error)
        notifications.error(error.message || 'Failed to set default group')
      }
    },

    hasFormIndicator(indicator) {
      return this.formData.indicators.some(ind => ind.type === indicator.type || ind.name === indicator.name)
    },

    toggleFormIndicator(indicator) {
      const index = this.formData.indicators.findIndex(ind =>
        ind.type === indicator.type || ind.name === indicator.name
      )
      if (index >= 0) {
        this.formData.indicators.splice(index, 1)
        return
      }
      this.formData.indicators.push({
        name: indicator.name,
        type: indicator.type || indicator.name,
        side: indicator.side || 'onchart',
        settings: indicator.settings || {},
        hidden: false
      })
    },

    removeFormIndicator(index) {
      this.formData.indicators.splice(index, 1)
    },

    toggleIndicatorVisibility(index) {
      const indicator = this.formData.indicators[index]
      this.$set(this.formData.indicators, index, {
        ...indicator,
        hidden: !indicator.hidden
      })
    },

    closeModal() {
      this.showFormModal = false
      this.editingGroup = null
      this.indicatorSearch = ''
      this.formData = { name: '', description: '', indicators: [], isDefault: false }
    },

    cancelDelete() {
      this.deletingGroup = null
    },

    // ── Sync group names with Legend's localStorage ──
    _getLegendGroups() {
      try {
        return JSON.parse(localStorage.getItem('tc_indicator_groups') || '[]')
      } catch { return [] }
    },
    _saveLegendGroups(arr) {
      localStorage.setItem('tc_indicator_groups', JSON.stringify(arr))
    },
    _syncLegendGroupAdd(name) {
      let arr = this._getLegendGroups()
      if (!arr.includes(name)) { arr.push(name); this._saveLegendGroups(arr) }
    },
    _syncLegendGroupRemove(name) {
      let arr = this._getLegendGroups()
      let i = arr.indexOf(name)
      if (i >= 0) { arr.splice(i, 1); this._saveLegendGroups(arr) }
    },
    _syncLegendGroupRename(oldName, newName) {
      let arr = this._getLegendGroups()
      let i = arr.indexOf(oldName)
      if (i >= 0) { arr[i] = newName } else { arr.push(newName) }
      this._saveLegendGroups(arr)
    }
  },
  watch: {
    currentIndicators: {
      handler(newIndicators) {
        if (this.showFormModal && !this.editingGroup) {
          this.formData.indicators = [...newIndicators]
        }
      },
      deep: true
    }
  }
}
</script>

<style scoped>
.group-manager {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

/* Header */
.gm-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 16px;
  flex-shrink: 0;
}

.gm-count {
  font-size: 13px;
  color: var(--tc-text-secondary);
  font-weight: 500;
}

.gm-new-btn {
  padding: 7px 16px;
  background: var(--tc-accent);
  color: #ffffff;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease;
}

.gm-new-btn:hover {
  background: var(--tc-accent-hover);
}

/* Loading */
.gm-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px;
}

.gm-spinner {
  width: 24px;
  height: 24px;
  border: 2px solid var(--tc-border-primary);
  border-top: 2px solid var(--tc-accent);
  border-radius: 50%;
  animation: gm-spin 0.8s linear infinite;
}

@keyframes gm-spin {
  to { transform: rotate(360deg); }
}

/* Empty */
.gm-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px 20px;
  text-align: center;
  color: var(--tc-text-tertiary);
}

.gm-empty svg {
  margin-bottom: 12px;
  opacity: 0.3;
}

.gm-empty-title {
  margin: 0 0 4px;
  font-size: 14px;
  font-weight: 600;
  color: var(--tc-text-secondary);
}

.gm-empty-desc {
  font-size: 12px;
  margin-bottom: 16px;
}

.gm-empty-action {
  padding: 8px 18px;
  background: var(--tc-accent);
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
}

.gm-empty-action:hover {
  background: var(--tc-accent-hover);
}

/* Groups List */
.gm-groups-list {
  flex: 1;
  overflow-y: auto;
  padding: 0 16px 16px;
}

.gm-groups-list::-webkit-scrollbar {
  width: 6px;
}

.gm-groups-list::-webkit-scrollbar-track {
  background: transparent;
}

.gm-groups-list::-webkit-scrollbar-thumb {
  background: var(--tc-scrollbar-thumb);
  border-radius: 3px;
}

.gm-group-card {
  padding: 14px 0;
  border-bottom: 1px solid var(--tc-border-secondary);
}

.gm-group-card:last-child {
  border-bottom: none;
}

.gm-group-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.gm-group-name {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 600;
  color: var(--tc-text-primary);
}

.gm-default-badge {
  padding: 2px 7px;
  border-radius: 999px;
  background: var(--tc-accent-bg);
  color: var(--tc-accent);
  font-size: 10px;
  font-weight: 700;
}

.gm-group-actions {
  display: flex;
  gap: 4px;
}

.gm-action-btn {
  width: 30px;
  height: 30px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
}

.gm-action-apply {
  background: var(--tc-accent);
  color: #ffffff;
}

.gm-action-apply:hover {
  background: var(--tc-accent-hover);
}

.gm-action-edit {
  background: var(--tc-bg-hover);
  color: var(--tc-text-secondary);
}

.gm-action-default {
  background: var(--tc-bg-hover);
  color: var(--tc-text-secondary);
}

.gm-action-default:hover,
.gm-action-default.active {
  background: var(--tc-accent-bg);
  color: var(--tc-accent);
}

.gm-action-edit:hover {
  background: var(--tc-bg-active);
  color: var(--tc-text-primary);
}

.gm-action-delete {
  background: var(--tc-bg-hover);
  color: var(--tc-text-secondary);
}

.gm-action-delete:hover {
  background: var(--tc-red-bg);
  color: var(--tc-red);
}

.gm-action-delete.disabled,
.gm-action-delete:disabled {
  cursor: not-allowed;
  opacity: 0.4;
}

.gm-action-delete.disabled:hover,
.gm-action-delete:disabled:hover {
  background: var(--tc-bg-hover);
  color: var(--tc-text-secondary);
}

/* Tags */
.gm-group-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.gm-tag {
  padding: 3px 10px;
  background: var(--tc-accent-bg);
  color: var(--tc-accent);
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
}

.gm-tag-more {
  background: var(--tc-bg-hover);
  color: var(--tc-text-secondary);
}

.gm-tag-hidden {
  opacity: 0.4;
  text-decoration: line-through;
}

/* Modal */
.gm-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10002;
}

.gm-modal {
  background: var(--tc-card-bg);
  border: 1px solid var(--tc-card-border);
  border-radius: 12px;
  width: 440px;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: var(--tc-shadow-lg);
}

.gm-modal-sm {
  width: 340px;
}

.gm-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid var(--tc-border-secondary);
}

.gm-modal-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--tc-text-primary);
}

.gm-modal-close {
  background: none;
  border: none;
  color: var(--tc-text-tertiary);
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
}

.gm-modal-close:hover {
  color: var(--tc-text-primary);
}

.gm-modal-body {
  padding: 16px;
}

.gm-form-group {
  margin-bottom: 14px;
}

.gm-form-group:last-child {
  margin-bottom: 0;
}

.gm-form-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: var(--tc-text-secondary);
  margin-bottom: 6px;
}

.gm-form-input,
.gm-form-textarea {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid var(--tc-input-border);
  border-radius: 6px;
  font-size: 13px;
  color: var(--tc-input-text);
  background: var(--tc-input-bg);
  outline: none;
  transition: border-color 0.15s ease;
  font-family: inherit;
}

.gm-form-input:focus,
.gm-form-textarea:focus {
  border-color: var(--tc-accent);
}

.gm-form-input::placeholder,
.gm-form-textarea::placeholder {
  color: var(--tc-input-placeholder);
}

.gm-form-textarea {
  resize: vertical;
  min-height: 48px;
}

.gm-default-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 14px;
  font-size: 12px;
  font-weight: 600;
  color: var(--tc-text-secondary);
  cursor: pointer;
}

.gm-default-toggle input {
  width: 14px;
  height: 14px;
  accent-color: var(--tc-accent);
}

.gm-picker {
  border: 1px solid var(--tc-border-secondary);
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 10px;
}

.gm-picker-search {
  padding: 8px;
  border-bottom: 1px solid var(--tc-border-secondary);
}

.gm-picker-list {
  max-height: 180px;
  overflow-y: auto;
}

.gm-picker-list::-webkit-scrollbar {
  width: 4px;
}

.gm-picker-list::-webkit-scrollbar-thumb {
  background: var(--tc-scrollbar-thumb);
  border-radius: 2px;
}

.gm-picker-item {
  width: 100%;
  min-height: 38px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 8px 10px;
  background: transparent;
  border: 0;
  border-bottom: 1px solid var(--tc-border-secondary);
  color: var(--tc-text-primary);
  cursor: pointer;
  text-align: left;
}

.gm-picker-item:last-child {
  border-bottom: 0;
}

.gm-picker-item:hover,
.gm-picker-item.selected {
  background: var(--tc-bg-hover);
}

.gm-picker-item span {
  display: flex;
  flex-direction: column;
  min-width: 0;
  font-size: 12px;
  font-weight: 600;
}

.gm-picker-item small {
  margin-top: 2px;
  color: var(--tc-text-tertiary);
  font-size: 10px;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.gm-empty-indicators {
  text-align: center;
  padding: 16px;
  color: var(--tc-text-tertiary);
  font-size: 12px;
}

.gm-empty-indicators p {
  margin: 0 0 4px;
  font-weight: 500;
  color: var(--tc-text-secondary);
}

.gm-empty-indicators span {
  font-size: 11px;
}

.gm-selected-indicators-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-height: 200px;
  overflow-y: auto;
}

.gm-selected-indicators-list::-webkit-scrollbar {
  width: 4px;
}

.gm-selected-indicators-list::-webkit-scrollbar-thumb {
  background: var(--tc-scrollbar-thumb);
  border-radius: 2px;
}

.gm-indicator-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  background: var(--tc-accent-bg);
  border-radius: 8px;
  transition: opacity 0.15s ease;
}

.gm-indicator-row.gm-indicator-hidden {
  opacity: 0.5;
}

.gm-visibility-btn {
  background: none;
  border: none;
  color: var(--tc-text-secondary);
  cursor: pointer;
  padding: 2px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: color 0.15s ease;
}

.gm-visibility-btn:hover {
  color: var(--tc-accent);
}

.gm-indicator-name {
  flex: 1;
  font-size: 12px;
  font-weight: 500;
  color: var(--tc-accent);
}

.gm-indicator-name.gm-name-hidden {
  text-decoration: line-through;
  color: var(--tc-text-tertiary);
}

.gm-selected-indicators {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.gm-selected-indicator {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  background: var(--tc-accent-bg);
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  color: var(--tc-accent);
}

.gm-remove-indicator {
  background: none;
  border: none;
  color: var(--tc-text-tertiary);
  cursor: pointer;
  padding: 2px;
  display: flex;
  align-items: center;
  flex-shrink: 0;
  border-radius: 4px;
  opacity: 0.6;
  transition: all 0.15s ease;
}

.gm-remove-indicator:hover {
  opacity: 1;
  color: var(--tc-red);
}

.gm-modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 16px;
  border-top: 1px solid var(--tc-border-secondary);
}

.gm-btn {
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
}

.gm-btn-secondary {
  background: var(--tc-bg-hover);
  color: var(--tc-text-primary);
}

.gm-btn-secondary:hover {
  background: var(--tc-bg-active);
}

.gm-btn-primary {
  background: var(--tc-accent);
  color: #ffffff;
}

.gm-btn-primary:hover:not(:disabled) {
  background: var(--tc-accent-hover);
}

.gm-btn-primary:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.gm-btn-danger {
  background: var(--tc-red);
  color: #ffffff;
}

.gm-btn-danger:hover {
  opacity: 0.9;
}

.gm-delete-msg {
  margin: 0;
  font-size: 13px;
  color: var(--tc-text-primary);
  line-height: 1.5;
}
</style>
